"use client";

import { Button, Input, Label, toast, useDebounceCallback } from "@devhop/ui";
import { PlusIcon, ScanBarcodeIcon, Trash2Icon } from "lucide-react";
import { useCallback, useId, useRef, useState } from "react";
import type { ShopSellableCatalogRow } from "@/modules/merchant/presentation/api/client";
import { shopCatalogApi } from "@/modules/merchant/presentation/api/client";
import { useLookupSellableByBarcodeMutation } from "@/modules/merchant/presentation/api/queries";
import { shopCurrencySymbol } from "@/shared/lib/currency";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

const PO_SCAN_MAX_LEN = 100;

export type PoDraftLine = {
  /** key ສຳລັບ React — id ເສັ້ນເກົ່າ ຫຼື uuid ຊົ່ວຄາວ */
  clientKey: string;
  shopProductSkuId: string;
  /** ປ່ອຍ "" ໄດ້ຂະນະພິມ — validate ≥ 1 ຕອນບັນທຶກ */
  qtyOrdered: number | "";
  unitCost: number | null;
  lineNote?: string;
};

type ShopPurchaseOrderDraftLinesEditorProps = {
  shopId: string;
  currency: string;
  lines: PoDraftLine[];
  onChange: (lines: PoDraftLine[]) => void;
  disabled?: boolean;
};

function newClientKey() {
  return crypto.randomUUID();
}

export function ShopPurchaseOrderDraftLinesEditor({
  shopId,
  currency,
  lines,
  onChange,
  disabled = false,
}: ShopPurchaseOrderDraftLinesEditorProps) {
  const lookup = useLookupSellableByBarcodeMutation(shopId);
  const barcodeFieldId = useId();
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const scanLockRef = useRef(false);
  const [barcode, setBarcode] = useState("");

  const updateLine = useCallback(
    (index: number, patch: Partial<PoDraftLine>) => {
      onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    },
    [lines, onChange],
  );

  const removeLine = useCallback(
    (index: number) => {
      onChange(lines.filter((_, i) => i !== index));
    },
    [lines, onChange],
  );

  const addEmptyLine = useCallback(() => {
    onChange([
      ...lines,
      {
        clientKey: newClientKey(),
        shopProductSkuId: "",
        qtyOrdered: 1,
        unitCost: null,
        lineNote: undefined,
      },
    ]);
  }, [lines, onChange]);

  const addLineFromScan = useCallback(
    (row: ShopSellableCatalogRow) => {
      if (row.currency !== currency) {
        toast.error("ສະກຸນເງິນສິນຄ້າບໍ່ກົງກັບຫົວ PO");
        return;
      }
      const i = lines.findIndex((l) => l.shopProductSkuId === row.id);
      if (i >= 0) {
        const next = lines.map((l, idx) => {
          if (idx !== i) return l;
          const prev = typeof l.qtyOrdered === "number" ? l.qtyOrdered : 0;
          return { ...l, qtyOrdered: prev + 1 };
        });
        onChange(next);
      } else {
        onChange([
          ...lines,
          {
            clientKey: newClientKey(),
            shopProductSkuId: row.id,
            qtyOrdered: 1,
            unitCost: null,
            lineNote: undefined,
          },
        ]);
      }
    },
    [currency, lines, onChange],
  );

  const runScan = useCallback(
    async (raw: string) => {
      const b = raw.trim();
      if (!b || disabled) return;
      if (b.length > PO_SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${PO_SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      if (scanLockRef.current) return;
      scanLockRef.current = true;
      try {
        const result = await lookup.mutateAsync(b);
        if (result.meta.total === 0) {
          toast.error("ບໍ່ພົບສິນຄ້າດ້ວຍ barcode ນີ້");
          return;
        }
        if (result.meta.total > 1) {
          toast.warning(`ພົບ ${result.meta.total} ລາຍການ — ໃຊ້ລາຍການທຳອິດ`);
        }
        const first = result.data[0];
        if (first) addLineFromScan(first);
        setBarcode("");
        queueMicrotask(() => barcodeInputRef.current?.focus());
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ຄົ້ນຫາລົ້ມເຫຼວ");
      } finally {
        scanLockRef.current = false;
      }
    },
    [disabled, lookup, addLineFromScan],
  );

  const runScanRef = useRef(runScan);
  runScanRef.current = runScan;

  const debouncedAutoScan = useDebounceCallback((value: string) => {
    const live = barcodeInputRef.current?.value ?? "";
    if (live !== value) return;
    void runScanRef.current(value);
  }, 90);

  const resolveBarcodeForLine = useCallback(
    async (lineIndex: number, raw: string) => {
      const b = raw.trim();
      if (!b || disabled) return;
      if (b.length > PO_SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${PO_SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      try {
        const result = await lookup.mutateAsync(b);
        if (result.meta.total === 0) {
          toast.error("ບໍ່ພົບສິນຄ້າດ້ວຍ barcode ນີ້");
          return;
        }
        if (result.meta.total > 1) {
          toast.warning(`ພົບ ${result.meta.total} ລາຍການ — ໃຊ້ລາຍການທຳອິດ`);
        }
        const row = result.data[0];
        if (!row) return;
        if (row.currency !== currency) {
          toast.error("ສະກຸນເງິນສິນຄ້າບໍ່ກົງກັບຫົວ PO");
          return;
        }
        const dupIdx = lines.findIndex(
          (l, i) => i !== lineIndex && l.shopProductSkuId === row.id,
        );
        if (dupIdx >= 0) {
          const q =
            typeof lines[lineIndex]?.qtyOrdered === "number"
              ? (lines[lineIndex]!.qtyOrdered as number)
              : 1;
          const next = lines
            .map((l, i) => {
              if (i !== dupIdx) return l;
              const prev = typeof l.qtyOrdered === "number" ? l.qtyOrdered : 0;
              return { ...l, qtyOrdered: prev + q };
            })
            .filter((_, i) => i !== lineIndex);
          onChange(next);
          toast.success("ລວມເຂົ້າແຖວທີ່ມີ SKU ນີ້ແລ້ວ");
          return;
        }
        updateLine(lineIndex, { shopProductSkuId: row.id });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ຄົ້ນຫາລົ້ມເຫຼວ");
      }
    },
    [currency, disabled, lines, lookup, onChange, updateLine],
  );

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <Label className="font-medium">ລາຍການສິນຄ້າ</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={disabled}
          onClick={addEmptyLine}
        >
          <PlusIcon className="size-3.5" aria-hidden />
          ເພີ່ມສິນຄ້າ
        </Button>
      </div>

      <div className="relative mb-4">
        <ScanBarcodeIcon
          className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-primary"
          aria-hidden
        />
        <Input
          ref={barcodeInputRef}
          id={barcodeFieldId}
          value={barcode}
          onChange={(e) => {
            const v = e.target.value;
            setBarcode(v);
            debouncedAutoScan(v);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            void runScan(e.currentTarget.value);
          }}
          placeholder="ລໍຖ້າສະແກນ barcode ຮ້ານ — ຫຼືພິມແລ້ວ Enter"
          className="h-10 border-border pr-3 pl-10 font-mono text-sm"
          autoComplete="off"
          disabled={disabled || lookup.isPending}
          aria-label="ສະແກນ barcode ເພື່ອເພີ່ມສິນຄ້າ"
        />
      </div>

      <div className="space-y-4">
        {lines.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີບັນທັດ — ສະແກນ barcode ຢູ່ຊ່ອງດ້ານເທິງ ຫຼື ເພີ່ມແຖວແລ້ວເລືອກ SKU
          </p>
        ) : null}
        {lines.map((row, index) => (
          <div
            key={row.clientKey}
            className="space-y-2 rounded-md border border-border/80 bg-card p-3"
          >
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-48 flex-1">
                <Label className="text-xs">SKU</Label>
                <InfiniteCombobox<ShopSellableCatalogRow>
                  queryKey={[
                    "shops",
                    shopId,
                    "po-draft-line",
                    row.clientKey,
                    currency,
                  ]}
                  queryFn={async ({ search: q, pageParam }) => {
                    const limit = 20;
                    const page = pageParam ?? 1;
                    const offset = (page - 1) * limit;
                    const res = await shopCatalogApi.listSellable(shopId, {
                      offset,
                      limit,
                      q: q.trim() || undefined,
                      barcode: undefined,
                      currency,
                    });
                    const next =
                      offset + res.data.length < res.meta.total
                        ? page + 1
                        : null;
                    return { items: res.data, nextPage: next };
                  }}
                  preloadQueryFn={(id) =>
                    shopCatalogApi.getSellableSku(shopId, id)
                  }
                  getLabel={(item) =>
                    `${item.productName} · ${item.shopSku} (${item.currency})`
                  }
                  getValue={(item) => item.id}
                  value={row.shopProductSkuId}
                  onValueChange={(id) => {
                    if (disabled) return;
                    updateLine(index, { shopProductSkuId: id });
                  }}
                  placeholder="ຄົ້ນຫາສິນຄ້າ..."
                  className="h-9 w-full"
                />
              </div>
              <div className="min-w-40 flex-1 sm:max-w-56">
                <Label className="text-xs">Barcode</Label>
                <div className="relative">
                  <ScanBarcodeIcon
                    className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-primary"
                    aria-hidden
                  />
                  <Input
                    className="h-9 w-full pl-8 font-mono text-xs"
                    placeholder="ສະແກນ ຫຼືພິມແລ້ວ Enter"
                    autoComplete="off"
                    disabled={disabled || lookup.isPending}
                    aria-label={`Barcode ບັນທັດ ${index + 1}`}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      const el = e.currentTarget;
                      const v = el.value;
                      void resolveBarcodeForLine(index, v).then(() => {
                        el.value = "";
                      });
                    }}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-destructive"
                disabled={disabled}
                onClick={() => removeLine(index)}
                aria-label="ລົບບັນທັດ"
              >
                <Trash2Icon className="size-4" />
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">
                  ຈຳນວນສັ່ງ <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  className="h-9"
                  value={row.qtyOrdered === "" ? "" : row.qtyOrdered}
                  disabled={disabled}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw.trim() === "") {
                      updateLine(index, { qtyOrdered: "" });
                      return;
                    }
                    const n = Number.parseInt(raw, 10);
                    if (!Number.isFinite(n)) return;
                    updateLine(index, {
                      qtyOrdered: n < 0 ? 0 : n,
                    });
                  }}
                  onBlur={() => {
                    if (
                      row.qtyOrdered === "" ||
                      !Number.isInteger(row.qtyOrdered) ||
                      row.qtyOrdered < 1
                    ) {
                      updateLine(index, { qtyOrdered: 1 });
                    }
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">
                  ຕົ້ນທຶນຕໍ່ຫົວຫນ່ວຍ ({shopCurrencySymbol(currency)})
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  className="h-9"
                  placeholder="—"
                  value={
                    row.unitCost != null
                      ? row.unitCost.toLocaleString("en-US")
                      : ""
                  }
                  disabled={disabled}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^0-9]/g, "");
                    updateLine(index, {
                      unitCost: digits === "" ? null : Number(digits),
                    });
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
